// Shared helpers for the Report Builder renderer: formatting, aggregation, chart options,
// CSV/XLSX export and print HTML. Pure functions - no component state.
import * as echarts from 'echarts';
import mergeWith from 'lodash/mergeWith';
import utils from '../../plugins/helpers';

const nf2 = new Intl.NumberFormat('en-TT', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
const nf0 = new Intl.NumberFormat('en-TT', { maximumFractionDigits: 0 });

/** Best human message from a failed axios call (the server sends { msg } for expected errors). */
export function errorText (e) {
  // the app's own helper understands every error shape the server and the interceptors produce
  return utils.extractErrorMessage(e)
}

export function formatValue(v, format) {
  if (v === null || v === undefined || v === '') return '';
  switch (format) {
    case 'currency': return utils.formatCurrency(v);
    case 'integer': return isNaN(v) ? String(v) : nf0.format(v);
    case 'number': return isNaN(v) ? String(v) : nf2.format(v);
    case 'percent': return isNaN(v) ? String(v) : `${nf2.format(v)}%`;
    case 'boolean': return v === true || v === 'true' ? 'Yes' : 'No';
    case 'date': return String(v).substring(0, 10);
    case 'datetime': return utils.formatDate ? utils.formatDate(v, 'withTime') : String(v);
    default:
      return typeof v === 'number' ? nf2.format(v) : String(v);
  }
}

export function aggregate(rows, field, how = 'first') {
  const vals = rows.map((r) => r[field]).filter((v) => v !== null && v !== undefined && v !== '');
  const nums = vals.map(Number).filter((n) => !isNaN(n));
  switch (how) {
    case 'sum': return nums.reduce((a, b) => a + b, 0);
    case 'avg': return nums.length ? nums.reduce((a, b) => a + b, 0) / nums.length : 0;
    case 'count': return rows.length;
    case 'max': return nums.length ? Math.max(...nums) : null;
    case 'min': return nums.length ? Math.min(...nums) : null;
    default: return rows.length ? rows[0][field] : null;
  }
}

/** Effective columns for a table section/export: explicit config, else every dataset column. */
export function columnsFor(configured, dataset) {
  if (configured && configured.length) {
    return configured.map((c) => ({ ...c, label: c.label || c.field }));
  }
  return ((dataset && dataset.columns) || []).map((f) => ({ field: f, label: f }));
}

export function totalsRow(columns, rows) {
  const out = {};
  columns.forEach((c, i) => {
    if (c.total && c.total !== 'none') out[c.field] = aggregate(rows, c.field, c.total);
    else out[c.field] = i === 0 ? 'Total' : '';
  });
  return out;
}

/** Build an echarts option from a chart section and its dataset rows. */
export function buildChartOption(section, rows) {
  const x = rows.map((r) => r[section.x]);
  const fmt = (v) => formatValue(v, section.valueFormat || 'number');
  let option;
  if (section.chart === 'pie') {
    const s = (section.series || [])[0] || {};
    option = {
      tooltip: { trigger: 'item', valueFormatter: fmt },
      legend: { type: 'scroll', bottom: 0 },
      series: [{
        type: 'pie', radius: ['35%', '65%'],
        data: rows.map((r) => ({ name: String(r[section.x]), value: Number(r[s.field]) || 0 }))
      }]
    };
  } else {
    option = {
      tooltip: { trigger: 'axis', valueFormatter: fmt },
      legend: { type: 'scroll', top: 0 },
      grid: { left: 8, right: 16, bottom: 8, top: 40, containLabel: true },
      xAxis: { type: 'category', data: x },
      yAxis: { type: 'value' },
      series: (section.series || []).map((s) => ({
        name: s.label || s.field,
        type: section.chart === 'bar' ? 'bar' : 'line',
        ...(section.chart === 'area' ? { areaStyle: {} } : {}),
        ...(section.stacked ? { stack: 'total' } : {}),
        data: rows.map((r) => Number(r[s.field]) || 0)
      }))
    };
  }
  const custom = section.echartsOption;
  if (!custom || typeof custom !== 'object') return option;
  // "replace": only the builder's own JSON is used; the rows are supplied as an ECharts dataset so series can use `encode`
  if (section.echartsMode === 'replace') {
    const own = JSON.parse(JSON.stringify(custom));
    if (!own.dataset) own.dataset = { source: rows };
    return own;
  }
  // "merge": the builder's JSON is laid over the chart the form made (arrays are replaced, objects merged)
  return mergeWith({}, option, custom, (a, b) => (Array.isArray(b) ? b : undefined));
}

/**
 * The chart the form makes, written as a standalone ECharts option that reads the report's rows through `dataset` + `encode`
 * (so it keeps working as data changes). Used to prefill the "replace" JSON for editing.
 */
export function chartStarterJson (section) {
  const series = (section.series || []).filter((s) => s.field)
  let option
  if (section.chart === 'pie') {
    const s = series[0] || {}
    option = {
      tooltip: { trigger: 'item' }, legend: { type: 'scroll', bottom: 0 },
      series: [{ type: 'pie', radius: ['35%', '65%'], encode: { itemName: section.x || '', value: s.field || '' } }]
    }
  } else {
    option = {
      tooltip: { trigger: 'axis' }, legend: { type: 'scroll', top: 0 },
      grid: { left: 8, right: 16, bottom: 8, top: 40, containLabel: true },
      xAxis: { type: 'category' }, yAxis: { type: 'value' },
      series: series.map((s) => ({
        name: s.label || s.field, type: section.chart === 'bar' ? 'bar' : 'line',
        ...(section.chart === 'area' ? { areaStyle: {} } : {}), ...(section.stacked ? { stack: 'total' } : {}),
        encode: { x: section.x || '', y: s.field }
      }))
    }
  }
  return JSON.stringify(option, null, 2)
}

// ---------- report details (filters, calculation details, run info) ----------
// Everything that explains how a number was produced travels with exports and prints.

function filterValueText (p, v, options) {
  const empty = v === null || v === undefined || v === '' || (Array.isArray(v) && !v.length)
  if (p.type === 'daterange') {
    const f = v && v.from
    const t = v && v.to
    return f || t ? `${f || '...'} to ${t || '...'}` : 'All dates'
  }
  if (empty) return p.type === 'boolean' ? 'No' : 'All'
  if (p.type === 'boolean') return v ? 'Yes' : 'No'
  if (p.type === 'select') {
    const opts = (p.options && p.options.length ? p.options : (options && options[p.name])) || []
    const label = (x) => { const o = opts.find((y) => String(y.value) === String(x)); return o ? o.label : String(x) }
    return Array.isArray(v) ? v.map(label).join(', ') : label(v)
  }
  return Array.isArray(v) ? v.join(', ') : String(v)
}

/** [{label, value}] for every filter of the report, using the values the user chose. */
export function describeFilters (params, values, options) {
  return (params || []).map((p) => ({ label: p.label || p.name, value: filterValueText(p, (values || {})[p.name], options) }))
}

/**
 * ctx: { template, drill, result, values, options, user, reportKey, version }
 * Returns { title, filters, info, run } where each is [{label, value, note?}].
 */
export function buildDetails (ctx) {
  const t = ctx.template
  const drill = ctx.drill ? (t.drilldowns || []).find((d) => d.id === ctx.drill) : null
  const run = [
    { label: 'Report', value: t.name },
    ...(drill ? [{ label: 'Data view', value: drill.name }] : []),
    { label: 'Report key', value: `${ctx.reportKey || t.key || ''}${ctx.version ? ` (version ${ctx.version})` : ''}` },
    { label: 'Generated', value: new Date(ctx.result.meta.generatedAt).toLocaleString() },
    { label: 'Generated by', value: ctx.user || '' }
  ]
  const truncated = Object.entries(ctx.result.datasets).filter(([, v]) => v.truncated).map(([k]) => k)
  if (truncated.length) run.push({ label: 'Warning', value: `Result was truncated (${truncated.join(', ')}); narrow the filters for complete data.` })
  return {
    title: t.name + (drill ? ` - ${drill.name}` : ''),
    filters: describeFilters(t.params, ctx.values, ctx.options),
    info: (ctx.result.info || []).map((i) => ({ label: i.label, value: formatValue(i.value, i.format), note: i.note })),
    run
  }
}

// ---------- export ----------

/** Columns for an export: explicit, else the matching table section's columns, else every column. */
export function exportColumns (exp, template, drillId, ds) {
  if (exp.columns && exp.columns.length) return exp.columns.map((c) => ({ ...c, label: c.label || c.field }))
  const drill = drillId ? (template.drilldowns || []).find((d) => d.id === drillId) : null
  const sections = (drill ? drill.ui : template.ui).sections
  const sec = sections.find((x) => x.type === 'table' && x.dataset === exp.dataset && x.columns && x.columns.length)
  return columnsFor(sec ? sec.columns.map((c) => ({ field: c.field, label: c.label })) : null, ds)
}

function exportRows (exp, ctx) {
  const ds = ctx.result.datasets[exp.dataset] || { columns: [], rows: [] }
  const cols = exportColumns(exp, ctx.template, ctx.drill, ds)
  return {
    count: ds.rows.length,
    header: cols.map((c) => c.label),
    body: ds.rows.map((r) => cols.map((c) => (r[c.field] === undefined || r[c.field] === null ? '' : r[c.field])))
  }
}

function detailRows (ctx, count) {
  const d = buildDetails(ctx)
  const rows = []
  rows.push(['REPORT DETAILS', ''])
  d.run.forEach((r) => rows.push([r.label, r.value]))
  rows.push(['Records in this file', count])
  rows.push(['', ''])
  rows.push(['FILTERS USED', ''])
  if (!d.filters.length) rows.push(['(none)', ''])
  d.filters.forEach((f) => rows.push([f.label, f.value]))
  if (d.info.length) {
    rows.push(['', ''])
    rows.push(['CALCULATION DETAILS', ''])
    d.info.forEach((i) => { rows.push([i.label, i.value]); if (i.note) rows.push(['', i.note]) })
  }
  return rows
}

function download (filename, blob) {
  const a = document.createElement('a')
  a.href = URL.createObjectURL(blob)
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  setTimeout(() => URL.revokeObjectURL(a.href), 2000)
}

const safeName = (s) => String(s || 'report').replace(/[^A-Za-z0-9._-]+/g, '_')
const baseName = (ctx, exp) => `${safeName(ctx.template.name)}${ctx.drill ? '-' + safeName(ctx.drill) : ''}-${safeName(exp.name)}`

export function exportCsv (exp, ctx) {
  const { header, body, count } = exportRows(exp, ctx)
  const esc = (v) => {
    const s = v === null || v === undefined ? '' : String(v)
    return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
  }
  const lines = []
  if (exp.details !== false) {
    detailRows(ctx, count).forEach((r) => lines.push(r.map(esc).join(',')))
    lines.push('')
  }
  lines.push(header.map(esc).join(','))
  body.forEach((r) => lines.push(r.map(esc).join(',')))
  download(`${baseName(ctx, exp)}.csv`, new Blob(['﻿' + lines.join('\r\n')], { type: 'text/csv;charset=utf-8' }))
}

export async function exportXlsx (exp, ctx) {
  const XLSX = await import(/* webpackChunkName: "rb-xlsx" */ 'xlsx')
  const { header, body, count } = exportRows(exp, ctx)
  const wb = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([header, ...body]), safeName(exp.name).substring(0, 31) || 'Data')
  if (exp.details !== false) {
    const ws = XLSX.utils.aoa_to_sheet(detailRows(ctx, count))
    ws['!cols'] = [{ wch: 42 }, { wch: 80 }]
    XLSX.utils.book_append_sheet(wb, ws, 'Report details')
  }
  XLSX.writeFile(wb, `${baseName(ctx, exp)}.xlsx`)
}

export function exportJson (exp, ctx) {
  const ds = ctx.result.datasets[exp.dataset] || { rows: [] }
  const d = buildDetails(ctx)
  const body = exp.details === false ? ds.rows : { report: d.run, filters: d.filters, calculationDetails: d.info, rows: ds.rows }
  download(`${baseName(ctx, exp)}.json`, new Blob([JSON.stringify(body, null, 2)], { type: 'application/json' }))
}

/**
 * Download one table's data as it is: raw numbers (no currency symbols or thousands separators), every configured
 * column, only the rows currently matching the table's search. Excel also gets a "Report details" sheet when ctx is given.
 */
export async function exportTable ({ type, name, columns, rows, ctx }) {
  const header = columns.map((c) => c.label || c.field)
  const body = rows.map((r) => columns.map((c) => (r[c.field] === undefined || r[c.field] === null ? '' : r[c.field])))
  const file = `${safeName(ctx ? ctx.template.name : 'report')}-${safeName(name)}`
  if (type === 'xlsx') {
    const XLSX = await import(/* webpackChunkName: "rb-xlsx" */ 'xlsx')
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([header, ...body]), safeName(name).substring(0, 31) || 'Data')
    if (ctx) {
      const ws = XLSX.utils.aoa_to_sheet(detailRows(ctx, rows.length))
      ws['!cols'] = [{ wch: 42 }, { wch: 80 }]
      XLSX.utils.book_append_sheet(wb, ws, 'Report details')
    }
    XLSX.writeFile(wb, `${file}.xlsx`)
    return
  }
  const esc = (v) => {
    const s = v === null || v === undefined ? '' : String(v)
    return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
  }
  const lines = [header, ...body].map((r) => r.map(esc).join(','))
  download(`${file}.csv`, new Blob(['\ufeff' + lines.join('\r\n')], { type: 'text/csv;charset=utf-8' }))
}

export function runExport (exp, ctx) {
  if (exp.type === 'xlsx') return exportXlsx(exp, ctx)
  if (exp.type === 'json') return exportJson(exp, ctx)
  return exportCsv(exp, ctx)
}

// ---------- print ----------

const esc = (s) => String(s === null || s === undefined ? '' : s)
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')

/** chartImages: { [sectionId]: dataUrl } captured from the live charts. ctx as in buildDetails. */
/**
 * The sections a print layout prints, in print order. Everything after a switched-off section moves up.
 * New style (hiddenSections present): `sections` is the full order, hiddenSections are skipped, and sections
 * added to the report later print at the end. Old style: `sections`, when set, is the list to print.
 */
export function printedSections (allSections, def) {
  const order = def.sections || []
  if (Array.isArray(def.hiddenSections)) {
    const hidden = new Set(def.hiddenSections)
    const known = order.map((id) => allSections.find((s) => s.id === id)).filter(Boolean)
    const extra = allSections.filter((s) => !order.includes(s.id))
    return [...known, ...extra].filter((s) => !hidden.has(s.id))
  }
  return order.length ? order.map((id) => allSections.find((s) => s.id === id)).filter(Boolean) : allSections
}

/** Pictures of the chart sections (drawn off-screen), keyed by section id - used by every print. */
export function renderChartImages (sections, result) {
  const out = {};
  for (const s of sections) {
    if (s.type !== 'chart') continue;
    const ds = result.datasets[s.dataset];
    if (!ds) continue;
    const el = document.createElement('div');
    el.style.cssText = 'position:absolute;left:-9999px;top:0;width:900px;height:360px';
    document.body.appendChild(el);
    const chart = echarts.init(el, null, { renderer: 'canvas', width: 900, height: 360 });
    chart.setOption({ ...buildChartOption(s, ds.rows), animation: false });
    out[s.id] = chart.getDataURL({ pixelRatio: 2, backgroundColor: '#fff' });
    chart.dispose();
    document.body.removeChild(el);
  }
  return out;
}

export const PAPER_MM = { A4: [210, 297], A5: [148, 210], Letter: [215.9, 279.4], Legal: [215.9, 355.6], '80mm': [80, 200] }

/** Page size in mm for a print layout, after orientation. */
export function paperSize (def) {
  const [w, h] = PAPER_MM[(def && def.paper) || 'A4'] || PAPER_MM.A4
  return def && def.orientation === 'landscape' && def.paper !== '80mm' ? [h, w] : [w, h]
}

/**
 * Print document for a report. opts.preview = true adds the page margin as padding so the layout looks the
 * same on screen (a real print gets the margin from @page).
 */
export function buildPrintHtml (ctx, printDef, chartImages = {}, opts = {}) {
  const def = printDef || {}
  const t = ctx.template
  const drill = ctx.drill ? (t.drilldowns || []).find((d) => d.id === ctx.drill) : null
  const allSections = (drill ? drill.ui : t.ui).sections
  const sections = printedSections(allSections, def)
  const paper = def.paper || 'A4'
  const size = paper === '80mm' ? '80mm auto' : `${paper} ${def.orientation || 'portrait'}`
  const header = def.header || {}
  const margin = def.margin == null ? 12 : def.margin
  const fontSize = def.fontSize || 11
  const details = buildDetails(ctx)
  const result = ctx.result
  const parts = []

  for (const s of sections) {
    const startAt = parts.length
    const title = s.title ? `<h2>${esc(s.title)}</h2>` : ''
    if (s.type === 'kpi') {
      const items = (s.items || []).map((it) => {
        const ds = result.datasets[it.dataset] || { rows: [] }
        return `<div class="kpi"><div class="kv">${esc(formatValue(aggregate(ds.rows, it.field, it.aggregate), it.format || 'number'))}</div><div class="kl">${esc(it.label)}</div></div>`
      }).join('')
      parts.push(`${title}<div class="kpis">${items}</div>`)
    } else if (s.type === 'table') {
      const ds = result.datasets[s.dataset] || { columns: [], rows: [] }
      const cols = columnsFor(s.columns, ds).filter((c) => !c.hidden)
      const head = cols.map((c) => `<th class="${c.align === 'right' ? 'r' : ''}">${esc(c.label)}</th>`).join('')
      const body = ds.rows.map((r) => `<tr>${cols.map((c) => `<td class="${c.align === 'right' ? 'r' : ''}">${esc(formatValue(r[c.field], c.format))}</td>`).join('')}</tr>`).join('')
      const hasTotals = s.showTotals && cols.some((c) => c.total && c.total !== 'none')
      const tot = hasTotals ? totalsRow(cols, ds.rows) : null
      const foot = tot ? `<tfoot><tr>${cols.map((c) => `<td class="${c.align === 'right' ? 'r' : ''}"><b>${esc(c.total && c.total !== 'none' ? formatValue(tot[c.field], c.format) : tot[c.field])}</b></td>`).join('')}</tr></tfoot>` : ''
      parts.push(`${title}<table><thead><tr>${head}</tr></thead><tbody>${body}</tbody>${foot}</table><div class="cnt">${ds.rows.length} record(s)</div>`)
    } else if (s.type === 'chart') {
      const img = chartImages[s.id]
      if (img) parts.push(`${title}<img class="chart" src="${img}">`)
    } else if (s.type === 'text') {
      parts.push(`${title}<p>${esc(s.content).replace(/\n/g, '<br>')}</p>`)
    }
    // each section sits in its own marked box, so the print designer can highlight it
    if (parts.length > startAt) parts.push(`<div data-rb="sec:${s.id}">${parts.splice(startAt).join('\n')}</div>`)
  }

  const filterHtml = header.params === false || !details.filters.length ? ''
    : `<div class="box" data-rb="el:params"><b>Filters</b>${details.filters.map((f) => `<span class="chip">${esc(f.label)}: <b>${esc(f.value)}</b></span>`).join('')}</div>`
  const infoHtml = header.info === false || !details.info.length ? ''
    : `<div class="box" data-rb="el:info"><b>Calculation details</b>${details.info.map((i) => `<span class="chip">${esc(i.label)}: <b>${esc(i.value)}</b></span>`).join('')}${details.info.filter((i) => i.note).map((i) => `<div class="note">${esc(i.label)}: ${esc(i.note)}</div>`).join('')}</div>`
  const runHtml = header.runInfo === false ? ''
    : `<div class="foot" data-rb="el:runInfo">${details.run.filter((r) => ['Report key', 'Generated', 'Generated by', 'Warning'].includes(r.label)).map((r) => `${esc(r.label)}: ${esc(r.value)}`).join('  |  ')}</div>`

  const css = `
    @page { size: ${size}; margin: ${margin}mm; }
    body { font-family: Arial, Helvetica, sans-serif; font-size: ${fontSize}px; color: #111; ${opts.preview ? `margin: 0; padding: ${margin}mm; box-sizing: border-box; width: ${paperSize(def)[0]}mm;` : ''} }
    h1 { font-size: 1.6em; margin: 0 0 4px; } .sub { color: #555; margin-bottom: 4px; } h2 { font-size: 13px; margin: 14px 0 4px; }
    .box { border: 1px solid #ddd; padding: 5px 8px; margin: 6px 0; } .box > b { margin-right: 8px; }
    .chip { display: inline-block; margin: 1px 10px 1px 0; } .note { color: #555; font-size: 10px; margin-top: 2px; }
    table { width: 100%; border-collapse: collapse; margin-bottom: 4px; }
    th, td { border-bottom: 1px solid #ddd; padding: 3px 5px; text-align: left; }
    th { background: #f2f2f2; } .r { text-align: right; } .cnt { color: #777; font-size: 10px; margin-bottom: 8px; }
    .kpis { display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 8px; }
    .kpi { border: 1px solid #ddd; padding: 6px 10px; min-width: 90px; }
    .kv { font-size: 15px; font-weight: bold; } .kl { color: #555; }
    img.chart { max-width: 100%; } thead { display: table-header-group; } tr { page-break-inside: avoid; }
    .foot { margin-top: 12px; color: #777; font-size: 10px; border-top: 1px solid #ddd; padding-top: 4px; }
    ${def.css || ''}`
  return `<!doctype html><html><head><meta charset="utf-8"><title>${esc(details.title)}</title><style>${css}</style></head><body>
    ${header.title === false ? '' : `<div data-rb="el:title"><h1>${esc(details.title)}</h1>${def.subtitle ? `<div class="sub">${esc(def.subtitle)}</div>` : ''}</div>`}
    ${filterHtml}${infoHtml}
    ${parts.join('\n')}
    ${(def.footer && def.footer.text) ? `<div class="foot" data-rb="el:footer">${esc(def.footer.text)}</div>` : ''}
    ${runHtml}
  </body></html>`
}

export function printHtml (html) {
  const iframe = document.createElement('iframe')
  iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;'
  document.body.appendChild(iframe)
  const doc = iframe.contentWindow.document
  doc.open()
  doc.write(html)
  doc.close()
  setTimeout(() => {
    iframe.contentWindow.focus()
    iframe.contentWindow.print()
    setTimeout(() => document.body.removeChild(iframe), 2000)
  }, 250)
}
